import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { formatDateIT } from "@/lib/dates";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CAUSE_CODES, CAUSE_LABELS, CAUSE_SHORT } from "@/lib/constants";
import { FileSpreadsheet, Upload, Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import { SuiteCard, SuiteButton, SuiteEyebrow } from "@/components/suite";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { downloadBlob } from "@/lib/download";
import { useEffectiveRole } from "@/foundation/context";
import { fmtHours } from "@/foundation/formatting";

const MAP_FIELDS = [
  { key: "date", label: "Data" },
  { key: "matricola", label: "Matricola" },
  { key: "employee_name", label: "Nome dipendente" },
  { key: "notes", label: "Note" },
  ...CAUSE_CODES.map((c) => ({ key: c, label: CAUSE_LABELS[c] })),
];

export default function ImportExcel() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [step, setStep] = useState(1); // 1=upload, 2=map+preview

  useEffect(() => {
    if (!["admin", "super_admin"].includes(effectiveRole)) return;
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const sorted = sortCompaniesIt(data);
        setCompanies(sorted);
        if (!companyId && sorted.length) setCompanyId(sorted[0].id);
      } catch (e) { toast.error(apiErr(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/imports/template", { responseType: "blob" });
      downloadBlob(res.data, "template_presenze.xlsx", res.headers?.["content-type"]);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const preview = async (newMapping) => {
    if (!file || !companyId) { toast.error("Seleziona file e azienda"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/imports/preview", fd, {
        params: { company_id: companyId, mapping: JSON.stringify(newMapping || mapping || {}) },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setColumns(res.data.columns || []);
      setMapping(res.data.detected_mapping || {});
      setRows(res.data.rows || []);
      setStep(2);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const applyMapping = async () => {
    await preview(mapping);
  };

  const confirmImport = async () => {
    const good = rows.filter((r) => r.employee_id && r.date);
    if (good.length === 0) { toast.error("Nessuna riga valida"); return; }
    setConfirming(true);
    try {
      const { data } = await api.post("/imports/confirm", {
        company_id: companyId,
        rows: good.map((r) => ({
          row_index: r.row_index, date: r.date, matricola: r.matricola,
          employee_name: r.employee_name, employee_id: r.employee_id,
          cause_hours: r.cause_hours || {}, notes: r.notes || null, warnings: [],
        })),
        status: "sent",
      });
      toast.success(`Import completato: ${data.inserted} inserite, ${data.updated} aggiornate, ${data.skipped} saltate`);
      setStep(1); setFile(null); setRows([]); setColumns([]); setMapping({});
    } catch (e) { toast.error(apiErr(e)); }
    finally { setConfirming(false); }
  };

  const totalOk = rows.filter((r) => r.employee_id && r.date && (r.warnings?.length ?? 0) === 0).length;
  const totalWarn = rows.filter((r) => r.warnings?.length > 0).length;
  const totalSkip = rows.filter((r) => !r.employee_id || !r.date).length;

  return (
    <div className="space-y-6 max-w-[1600px]">
      <div>
        <SuiteEyebrow icon={FileSpreadsheet}>Importazione</SuiteEyebrow>
        <h1 className="text-3xl font-bold tracking-tight mt-1 text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>Import Excel</h1>
        <p className="text-sm text-suite-neutral-500 mt-1">Carica il tabulato mensile, mappa le colonne, verifica anteprima e conferma.</p>
      </div>

      {step === 1 && (
        <SuiteCard padding="lg" testid="import-step1-card">
          <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
              {["admin", "super_admin"].includes(effectiveRole) && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-suite-neutral-500 block mb-1">Azienda destinazione</Label>
                  <Select value={companyId || ""} onValueChange={setCompanyId}>
                    <SelectTrigger className="w-72" data-testid="import-company-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <SuiteButton variant="secondary" onClick={downloadTemplate} icon={Download} data-testid="download-template-btn">
                Scarica template Excel
              </SuiteButton>
            </div>

            <div
              className="border-2 border-dashed border-suite-neutral-300 rounded-md p-8 text-center bg-suite-neutral-50 hover:bg-suite-neutral-100 transition-colors cursor-pointer"
              onClick={() => document.getElementById("excel-input").click()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
              onDragOver={(e) => e.preventDefault()}
              data-testid="import-dropzone"
            >
              <FileSpreadsheet size={40} className="mx-auto text-suite-primary mb-2" />
              <div className="text-sm font-semibold text-suite-neutral-800">
                {file ? file.name : "Trascina il file Excel qui o clicca per selezionarlo"}
              </div>
              <div className="text-xs text-suite-neutral-500 mt-1">Formato .xlsx supportato</div>
              <input id="excel-input" type="file" accept=".xlsx" className="hidden"
                     onChange={(e) => setFile(e.target.files?.[0])} data-testid="import-file-input" />
            </div>

            <div className="flex justify-end">
              <SuiteButton onClick={() => preview({})} disabled={!file || !companyId || loading} icon={Upload} loading={loading} data-testid="import-preview-btn">
                {loading ? "Analisi…" : "Analizza file"}
              </SuiteButton>
            </div>
          </div>
        </SuiteCard>
      )}

      {step === 2 && (
        <>
          <SuiteCard padding="md" testid="import-mapping-card">
            <div className="space-y-4">
              <div className="text-sm font-semibold text-suite-neutral-800">Mappatura colonne</div>
              <p className="text-xs text-suite-neutral-500">
                Il sistema ha tentato una mappatura automatica. Correggila se necessario e ricalcola l&apos;anteprima.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {MAP_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    <Select value={mapping[f.key] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="text-sm" data-testid={`map-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— non usata —</SelectItem>
                        {columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="flex justify-between">
                <SuiteButton variant="secondary" onClick={() => setStep(1)}>Cambia file</SuiteButton>
                <SuiteButton onClick={applyMapping} variant="secondary" data-testid="import-recalc-btn">Ricalcola anteprima</SuiteButton>
              </div>
            </div>
          </SuiteCard>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-emerald-200 rounded-md p-3 bg-emerald-50">
              <div className="text-xs uppercase tracking-widest text-emerald-700">Pronte</div>
              <div className="text-2xl font-mono font-bold text-emerald-900">{totalOk}</div>
            </div>
            <div className="border border-amber-200 rounded-md p-3 bg-amber-50">
              <div className="text-xs uppercase tracking-widest text-amber-700">Con avvisi</div>
              <div className="text-2xl font-mono font-bold text-amber-900">{totalWarn}</div>
            </div>
            <div className="border border-red-200 rounded-md p-3 bg-red-50">
              <div className="text-xs uppercase tracking-widest text-red-700">Saltate</div>
              <div className="text-2xl font-mono font-bold text-red-900">{totalSkip}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3 bg-white">
              <div className="text-xs uppercase tracking-widest text-slate-500">Totale righe</div>
              <div className="text-2xl font-mono font-bold">{rows.length}</div>
            </div>
          </div>

          <SuiteCard padding="none" className="overflow-hidden overflow-x-auto" testid="import-preview-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-widest">#</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Data</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Matricola</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Nome</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Dipendente</TableHead>
                    {CAUSE_CODES.map((c) => (
                      <TableHead key={c} className="text-xs uppercase tracking-widest text-right">{CAUSE_SHORT[c]}</TableHead>
                    ))}
                    <TableHead className="text-xs uppercase tracking-widest">Avvisi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.row_index} className={r.warnings?.length ? "bg-amber-50/40" : ""}>
                      <TableCell className="font-mono text-slate-500">{r.row_index}</TableCell>
                      <TableCell className="font-mono">{formatDateIT(r.date, <span className="text-red-700">—</span>)}</TableCell>
                      <TableCell className="font-mono">{r.matricola || "—"}</TableCell>
                      <TableCell>{r.employee_name || "—"}</TableCell>
                      <TableCell>
                        {r.employee_id
                          ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200"><CheckCircle2 size={12} className="mr-1" /> abbinato</Badge>
                          : <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200">non trovato</Badge>}
                      </TableCell>
                      {CAUSE_CODES.map((c) => (
                        <TableCell key={c} className="text-right font-mono text-sm">
                          {r.cause_hours?.[c] ? fmtHours(r.cause_hours[c]) : ""}
                        </TableCell>
                      ))}
                      <TableCell>
                        {(r.warnings || []).map((w, i) => (
                          <div key={i} className="text-xs text-amber-800 flex items-center gap-1">
                            <AlertTriangle size={12} /> {w}
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && <TableRow><TableCell colSpan={CAUSE_CODES.length + 6} className="text-center text-slate-400 py-8">Nessuna riga</TableCell></TableRow>}
                </TableBody>
              </Table>
          </SuiteCard>

          <div className="flex justify-end gap-2">
            <SuiteButton variant="secondary" onClick={() => setStep(1)}>Annulla</SuiteButton>
            <SuiteButton onClick={confirmImport} disabled={confirming || totalOk === 0} icon={CheckCircle2} loading={confirming} data-testid="import-confirm-btn">
              {confirming ? "Importo…" : `Conferma import (${totalOk + totalWarn} righe)`}
            </SuiteButton>
          </div>
        </>
      )}
    </div>
  );
}

